// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { Loader2, Power, PowerOff, TriangleAlert } from "lucide-react";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useToggleStoreActiveMutation } from "@/api/hooks/use-stores";
import { WizardPane } from "@/components/shared/wizard-pane";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface ToggleStoreActiveDialogProps {
  store: { storeName: string; legibleName: string; isActive: boolean } | null;
  onClose: () => void;
}

export function ToggleStoreActiveDialog({ store, onClose }: ToggleStoreActiveDialogProps) {
  const { t } = useTranslation("back-office");
  const lastStore = useRef(store);
  if (store) lastStore.current = store;
  const display = store ?? lastStore.current;
  const toggleActive = useToggleStoreActiveMutation(display?.storeName ?? "");

  async function handleToggleActive() {
    if (!display) return;
    const newActive = !display.isActive;
    try {
      await toggleActive.mutateAsync(newActive);
      toast.success(
        newActive ? `Store "${display.legibleName}" activated` : `Store "${display.legibleName}" deactivated`,
      );
      onClose();
    } catch {
      // Error toast handled by API middleware
    }
  }

  return (
    <Dialog open={!!store} onOpenChange={() => onClose()}>
      <DialogContent
        id="dialog-toggle-store-status"
        className="sm:max-w-[33rem] p-0 gap-0 overflow-hidden flex flex-col"
      >
        <div className="flex flex-row">
          <WizardPane
            variant={display?.isActive ? "destructive" : "default"}
            icon={display?.isActive ? PowerOff : Power}
          />
          <div className="flex-1 p-6 flex flex-col gap-4 min-w-0">
            <DialogHeader>
              <DialogTitle>
                {display?.isActive ? t("stores.dialog.deactivateTitle") : t("stores.dialog.activateTitle")}
              </DialogTitle>
              <DialogDescription asChild>
                <div>
                  {display?.isActive ? (
                    <>
                      <p>
                        {t("stores.dialog.deactivatePrompt")}
                        <br />
                        <span className="font-semibold text-foreground">{display?.legibleName}</span>?
                      </p>
                      <div className="mt-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200">
                        <TriangleAlert className="h-4 w-4 shrink-0 mt-0.5" />
                        <div className="text-sm space-y-1">
                          <p>{t("stores.dialog.deactivateConsequencesIntro")}</p>
                          <ul className="list-disc pl-5 space-y-1">
                            <li>{t("stores.dialog.deactivateConsequence1")}</li>
                            <li>{t("stores.dialog.deactivateConsequence2")}</li>
                          </ul>
                        </div>
                      </div>
                    </>
                  ) : (
                    <p>
                      {t("stores.dialog.activatePrompt")}
                      <br />
                      <span className="font-semibold text-foreground">{display?.legibleName}</span>?
                    </p>
                  )}
                </div>
              </DialogDescription>
            </DialogHeader>
          </div>
        </div>
        <DialogFooter className="mx-0 mb-0 rounded-none rounded-b-lg">
          <Button variant="outline" onClick={onClose}>
            {t("buttons.cancel")}
          </Button>
          <Button
            variant={display?.isActive ? "destructive" : "default"}
            className={
              display?.isActive
                ? ""
                : "bg-green-700 text-white hover:bg-green-800 dark:bg-green-700 dark:hover:bg-green-600"
            }
            onClick={handleToggleActive}
            disabled={toggleActive.isPending}
          >
            {toggleActive.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : display?.isActive ? (
              <PowerOff className="h-4 w-4" />
            ) : (
              <Power className="h-4 w-4" />
            )}
            {display?.isActive ? t("stores.dialog.deactivateConfirm") : t("stores.dialog.activateConfirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
